import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { Error404PageWithTranslations } from '@components/Error/Error404PageWithTranslations';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { noddiAsync, URLKeys } from 'noddi-async';
import { customerRoutes } from 'noddi-util';
import { dedupeSalesItemsByPublicName, useAuthContext } from 'noddi-web';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { UserAlreadyAMember } from '../../../components/Memberships/UserAlreadyAMember';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { ActivateMembershipCard } from './ActivateMembershipCard';
import { HighlightedSalesItem } from './HighlightedSalesItem';

export const Membership = () => {
  const { currentUserGroupId } = useAuthContext();
  const { slug } = useParams();

  return <MembershipContent slug={slug!} userGroupId={currentUserGroupId} />;
};

const MembershipContent = ({ slug, userGroupId }: { slug: string; userGroupId?: number }) => {
  const { isLoggedIn } = useAuthContext();
  const { currentBrand } = useCurrentBrand();

  const {
    data: membership,
    error: membershipError,
    isPending: isMembershipPending,
    isFetching: isMembershipFetching
  } = noddiAsync.useGet({
    type: URLKeys.getMembershipFromSlug,
    input: { slug, userGroupId }
  });

  // Like a campaign, a membership is claimed only on its owner brand's site.
  const isOtherBrand = membership != null && membership.brandDomain !== currentBrand;
  const campaignCode = isOtherBrand ? null : membership?.campaignCode;

  useEffect(() => {
    if (campaignCode) {
      window.location.replace(customerRoutes.campaignFromCode.getPath({ code: campaignCode }));
    }
  }, [campaignCode]);

  if (isMembershipPending || isMembershipFetching || campaignCode) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (isOtherBrand || membershipError?.status === 404) {
    return <Error404PageWithTranslations />;
  }

  if (membershipError) {
    return <ApiErrorPageWithTrans error={membershipError} />;
  }

  return (
    <ContentWrapperCustomerApp hideBackButton title={membership.name}>
      <div className='flex flex-col gap-6'>
        {isLoggedIn && membership.isAlreadyMember ? (
          <UserAlreadyAMember />
        ) : (
          <p className='-mt-4'>{membership.description}</p>
        )}

        {!membership.isAlreadyMember && <ActivateMembershipCard slug={slug} brandDomain={membership.brandDomain} />}

        <div className='flex flex-col gap-4'>
          {membership.discounts.map((discount) => (
            <div key={discount.description} className='flex flex-col gap-4'>
              <p className='text-xl font-semibold'>{discount.name}</p>
              {dedupeSalesItemsByPublicName(
                discount.highlightedSalesItems.filter(
                  (item) => item.bookingCategoryType != null && item.price?.priceGross?.amount != null
                )
              ).map((highlightedSalesItem) => (
                <HighlightedSalesItem
                  key={highlightedSalesItem.id}
                  serviceName={highlightedSalesItem.name}
                  price={highlightedSalesItem.price?.priceGross?.amount}
                  type={highlightedSalesItem.bookingCategoryType!}
                  discountedPrice={highlightedSalesItem.discountedPrice?.priceGross?.amount}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </ContentWrapperCustomerApp>
  );
};
